import React, { useState, useEffect } from 'react';
import { windowValueToPickerParts, pickerToWindowValue, localYMD } from '../utils/dbTime';

/**
 * DateTimePicker - A user-friendly date and time selection widget
 * Combines a date picker with easy hour/minute dropdowns
 */

const containerStyle = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  flexWrap: 'wrap'
};

const dateInputStyle = {
  padding: '0.5rem',
  backgroundColor: 'var(--surface-2)',
  border: '1px solid var(--border-default)',
  borderRadius: '5px',
  color: 'var(--text-primary)',
  fontSize: '0.9rem',
  minWidth: '140px'
};

const selectStyle = {
  padding: '0.5rem',
  backgroundColor: 'var(--surface-2)',
  border: '1px solid var(--border-default)',
  borderRadius: '5px',
  color: 'var(--text-primary)',
  fontSize: '0.9rem',
  cursor: 'pointer',
  minWidth: '70px'
};

const labelStyle = {
  color: 'var(--text-muted)',
  fontSize: '0.8rem'
};

// Generate hour options (12-hour format with AM/PM)
const generateHourOptions = () => {
  const options = [];
  for (let i = 0; i < 24; i++) {
    const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
    const ampm = i < 12 ? 'AM' : 'PM';
    options.push({
      value: i,
      label: `${hour12} ${ampm}`
    });
  }
  return options;
};

// Generate minute options (15-minute intervals for easier selection)
const generateMinuteOptions = () => {
  return [
    { value: 0, label: ':00' },
    { value: 15, label: ':15' },
    { value: 30, label: ':30' },
    { value: 45, label: ':45' }
  ];
};

const hourOptions = generateHourOptions();
const minuteOptions = generateMinuteOptions();

// Quick time presets
const timePresets = [
  { label: '6 AM', hour: 6, minute: 0 },
  { label: '8 AM', hour: 8, minute: 0 },
  { label: '12 PM', hour: 12, minute: 0 },
  { label: '5 PM', hour: 17, minute: 0 }
];

function DateTimePicker({ value, onChange, placeholder = 'Select date & time' }) {
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('');
  const [minute, setMinute] = useState('');
  const [showPresets, setShowPresets] = useState(false);

  // Parse incoming value (ISO datetime string)
  useEffect(() => {
    if (value) {
      try {
        // __TZ_WINDOWS__ a stored window is Toronto wall clock (as the hub writes it):
        // the picker shows those very hours. (__TZ_PICKER__ read it as UTC: -4/-5 h.)
        const p = windowValueToPickerParts(value);
        if (p) {
          setDate(p.date);
          setHour(p.hour.toString());
          // Round to nearest 15 minutes
          const roundedMins = Math.round(p.minute / 15) * 15;
          setMinute((roundedMins === 60 ? 0 : roundedMins).toString());
        }
      } catch (e) {
        // Invalid date, reset
        setDate('');
        setHour('');
        setMinute('');
      }
    } else {
      setDate('');
      setHour('');
      setMinute('');
    }
  }, [value]);

  // Build and emit datetime string when components change
  const emitChange = (newDate, newHour, newMinute) => {
    if (newDate && newHour !== '' && newMinute !== '') {
      // __TZ_WINDOWS__ saved as Toronto wall clock 'YYYY-MM-DDTHH:MM:00', the hub's own
      // shape (time_windows.iso_at), built from the parts so it cannot slip a day.
      const isoString = pickerToWindowValue(newDate, newHour, newMinute);
      if (isoString) onChange(isoString);
    } else if (!newDate && newHour === '' && newMinute === '') {
      onChange('');
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    emitChange(newDate, hour, minute);
  };

  const handleHourChange = (e) => {
    const newHour = e.target.value;
    setHour(newHour);
    // Auto-set minute to :00 if not set
    const newMinute = minute === '' ? '0' : minute;
    if (minute === '') setMinute('0');
    emitChange(date, newHour, newMinute);
  };

  const handleMinuteChange = (e) => {
    const newMinute = e.target.value;
    setMinute(newMinute);
    emitChange(date, hour, newMinute);
  };

  const handlePresetClick = (preset) => {
    // Use today's date if no date selected
    const useDate = date || localYMD();   // __TZ_PICKER__ the local today, not the UTC one
    setDate(useDate);
    setHour(preset.hour.toString());
    setMinute(preset.minute.toString());
    emitChange(useDate, preset.hour.toString(), preset.minute.toString());
    setShowPresets(false);
  };

  const handleClear = () => {
    setDate('');
    setHour('');
    setMinute('');
    onChange('');
  };

  return (
    <div>
      <div style={containerStyle}>
        {/* Date Input */}
        <input
          type="date"
          value={date}
          onChange={handleDateChange}
          style={dateInputStyle}
          placeholder="Date"
        />

        {/* Hour Select */}
        <select
          value={hour}
          onChange={handleHourChange}
          style={selectStyle}
        >
          <option value="">Hr</option>
          {hourOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Minute Select */}
        <select
          value={minute}
          onChange={handleMinuteChange}
          style={selectStyle}
          disabled={hour === ''}
        >
          <option value="">Min</option>
          {minuteOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Quick Presets Button */}
        <button
          type="button"
          onClick={() => setShowPresets(!showPresets)}
          style={{
            padding: '0.5rem',
            backgroundColor: 'var(--surface-3)',
            border: '1px solid var(--border-default)',
            borderRadius: '5px',
            color: 'var(--accent-primary)',
            fontSize: '0.8rem',
            cursor: 'pointer'
          }}
          title="Quick time presets"
        >
          Quick
        </button>

        {/* Clear Button */}
        {value && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--color-danger)',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
            title="Clear"
          >
            x
          </button>
        )}
      </div>

      {/* Quick Presets Dropdown */}
      {showPresets && (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '0.5rem',
          flexWrap: 'wrap'
        }}>
          {timePresets.map((preset, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handlePresetClick(preset)}
              style={{
                padding: '0.4rem 0.8rem',
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--accent-primary)',
                borderRadius: '15px',
                color: 'var(--accent-primary)',
                fontSize: '0.8rem',
                cursor: 'pointer'
              }}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              const now = new Date();
              const useDate = now.toISOString().slice(0, 10);
              const useHour = now.getHours();
              const useMinute = Math.round(now.getMinutes() / 15) * 15;
              setDate(useDate);
              setHour(useHour.toString());
              setMinute((useMinute === 60 ? 0 : useMinute).toString());
              emitChange(useDate, useHour.toString(), (useMinute === 60 ? 0 : useMinute).toString());
              setShowPresets(false);
            }}
            style={{
              padding: '0.4rem 0.8rem',
              backgroundColor: 'var(--btn-primary-bg)',
              border: 'none',
              borderRadius: '15px',
              color: 'var(--text-primary)',
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Now
          </button>
        </div>
      )}
    </div>
  );
}

export default DateTimePicker;
